import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { sessionService } from '../../services/api';

const FONT = "'Plus Jakarta Sans', sans-serif";

// task-n8 — the SoD-screen Back affordance's own scoped styles (this file is
// otherwise 100% inline-styled; a tiny <style> block is the cheapest way to
// get the RTL icon flip the rest of the native POS shell uses elsewhere,
// e.g. OperatorLockScreen.js's `[dir="rtl"] svg{transform:scaleX(-1)}`).
const BACK_CSS = `
.sod-back{position:absolute;top:16px;inset-inline-start:16px;min-height:44px;min-width:44px;
  padding:0 14px;border-radius:12px;border:1px solid #334155;background:rgba(30,41,59,0.85);
  color:#e2e8f0;display:flex;align-items:center;gap:6px;cursor:pointer;
  font-family:inherit;font-size:13px;font-weight:700;z-index:1;}
.sod-back:active{background:rgba(51,65,85,0.9);}
[dir="rtl"] .sod-back svg{transform:scaleX(-1);}
`;

const BackArrowIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 12H5M11 18l-6-6 6-6" />
    </svg>
);

// `fitContainer` (additive, default false — legacy callers are byte-
// identical): the legacy web/Electron page always renders this screen as a
// true full-viewport route, so `minHeight:'100vh'` is correct there. The
// native cashier surface (F12a-N Task 8) instead renders it INSIDE the
// station's bounded content slot (`.slf-fill`, itself `flex:1;min-height:0`)
// — forcing 100vh there makes the card taller than the actual slot, pushing
// the CTA below the fold even though the slot itself scrolls. Passing
// `fitContainer` swaps the minHeight to '100%' (fills the flex-item's real,
// already-bounded height) without touching anything else.
const StartOfDayScreen = ({ employeeId, employeeName, salesDate, onSessionStarted, onBack, fitContainer = false }) => {
    const { t } = useLanguage();
    const { getCurrency } = useAuth();
    const currency = getCurrency();

    const [startCash, setStartCash] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const formattedDate = (() => {
        try {
            const datePart = salesDate.split(' ')[0];
            const d = new Date(datePart + 'T00:00:00');
            const dateStr = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            const timeStr = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            return `${dateStr} · ${timeStr}`;
        } catch { return salesDate; }
    })();

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            const session = await sessionService.createSession({
                employee_id: employeeId,
                employee_name: employeeName,
                sales_date: salesDate,
                start_cash: parseFloat(startCash) || 0,
            });
            if (onSessionStarted) onSessionStarted(session);
        } catch (err) {
            console.error('Failed to create session:', err);
            const msg = err?.response?.data?.detail || 'Failed to start session';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'relative',
            minHeight: fitContainer ? '100%' : '100vh',
            background: '#0f172a',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: FONT,
            padding: 24,
        }}>
            {onBack && (
                <>
                    <style>{BACK_CSS}</style>
                    <button type="button" className="sod-back" onClick={onBack} aria-label={t('back') || 'Back'}>
                        {BackArrowIcon}
                        {t('back') || 'Back'}
                    </button>
                </>
            )}
            <div style={{
                width: '100%',
                maxWidth: 400,
                borderRadius: 14,
                overflow: 'hidden',
                background: '#1e293b',
                border: '1px solid #334155',
                boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
            }}>
                {/* Header */}
                <div style={{
                    background: '#2563eb',
                    padding: '18px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>
                            {t('start_your_session') || 'Start Your Session'}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                            {employeeName}
                        </div>
                    </div>
                    <div style={{
                        fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)',
                        background: 'rgba(255,255,255,0.15)',
                        padding: '5px 12px', borderRadius: 6,
                    }}>
                        {formattedDate}
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: '24px 24px 28px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.8,
                        color: '#94a3b8', marginBottom: 8,
                    }}>
                        {t('starting_cash') || 'Starting Cash'} ({currency})
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={startCash}
                        onChange={(e) => setStartCash(e.target.value)}
                        autoFocus
                        style={{
                            width: '100%',
                            padding: '14px 16px',
                            borderRadius: 10,
                            border: '1px solid #475569',
                            background: '#0f172a',
                            color: '#f8fafc',
                            fontSize: 22, fontWeight: 800,
                            textAlign: 'center',
                            outline: 'none',
                            fontFamily: FONT,
                            boxSizing: 'border-box',
                        }}
                    />
                    <div style={{
                        fontSize: 11, color: '#64748b',
                        textAlign: 'center', marginTop: 8,
                    }}>
                        {t('count_your_drawer') || 'Count your drawer and enter the total'}
                    </div>

                    {error && (
                        <div style={{
                            marginTop: 14, padding: '10px 14px',
                            borderRadius: 8,
                            background: 'rgba(220,38,38,0.1)',
                            border: '1px solid rgba(220,38,38,0.3)',
                            color: '#f87171', fontSize: 13, textAlign: 'center',
                        }}>
                            {error}
                        </div>
                    )}

                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            width: '100%',
                            marginTop: 20, padding: '14px 0',
                            borderRadius: 10, border: 'none',
                            background: loading ? '#334155' : '#2563eb',
                            color: loading ? '#64748b' : '#fff',
                            fontSize: 14, fontWeight: 800,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontFamily: FONT,
                        }}
                    >
                        {loading ? '...' : (t('start_session') || 'Start Session')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StartOfDayScreen;
